import { useState } from 'react';
import { Phone, Mail, MapPin, Send } from 'lucide-react';
import { mockData } from '../mock';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { useToast } from '../hooks/use-toast';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ContactSection = ({ onCallClick }) => {
  const { toast } = useToast();
  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
    preferredContact: 'phone'
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await axios.post(`${API}/contact`, {
        name: contactForm.name,
        email: contactForm.email,
        phone: contactForm.phone,
        message: contactForm.message,
        preferred_contact: contactForm.preferredContact
      });
      toast({
        title: "Request Submitted!",
        description: "We'll contact you within 24 hours.",
      });
      setContactForm({ name: '', email: '', phone: '', message: '', preferredContact: 'phone' });
    } catch {
      toast({
        title: "Submission Failed",
        description: "Please try again or call us directly.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setContactForm(prev => ({ ...prev, [name]: value }));
  };

  return (
    <section id="contact" className="py-20 bg-gradient-to-br from-blue-50 via-white to-teal-50">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <h3 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Get Your Free Quote</h3>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">Ready to explore your insurance options? Contact us for a free consultation</p>
        </div>
        <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Contact Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-start space-x-3">
                  <Phone className="h-5 w-5 text-blue-600 mt-1" />
                  <div>
                    <p className="font-semibold text-gray-900">Phone</p>
                    <a href={`tel:${mockData.company.phoneTel}`} className="text-blue-600 hover:underline" onClick={onCallClick}>
                      {mockData.company.phone}
                    </a>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <Mail className="h-5 w-5 text-blue-600 mt-1" />
                  <div>
                    <p className="font-semibold text-gray-900">Email</p>
                    <a href={`mailto:${mockData.company.email}`} className="text-blue-600 hover:underline">{mockData.company.email}</a>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <MapPin className="h-5 w-5 text-blue-600 mt-1" />
                  <div>
                    <p className="font-semibold text-gray-900">Service Area</p>
                    <p className="text-gray-600">{mockData.company.address}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader>
              <CardTitle>Book a Free Medicare Review</CardTitle>
              <CardDescription>Fill out the form and we'll reach out within 24 hours</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleContactSubmit} className="space-y-4" data-testid="contact-form">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                  <Input name="name" value={contactForm.name} onChange={handleInputChange} required placeholder="Your full name" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                  <Input type="email" name="email" value={contactForm.email} onChange={handleInputChange} required placeholder="your.email@example.com" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Phone *</label>
                  <Input type="tel" name="phone" value={contactForm.phone} onChange={handleInputChange} required placeholder="(555) 123-4567" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Message</label>
                  <Textarea name="message" value={contactForm.message} onChange={handleInputChange} placeholder="Tell us about your insurance needs..." rows={4} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Preferred Contact Method</label>
                  <div className="flex space-x-4">
                    <label className="flex items-center space-x-2">
                      <input type="radio" name="preferredContact" value="phone" checked={contactForm.preferredContact === 'phone'} onChange={handleInputChange} className="text-blue-600" />
                      <span className="text-sm text-gray-700">Phone</span>
                    </label>
                    <label className="flex items-center space-x-2">
                      <input type="radio" name="preferredContact" value="email" checked={contactForm.preferredContact === 'email'} onChange={handleInputChange} className="text-blue-600" />
                      <span className="text-sm text-gray-700">Email</span>
                    </label>
                  </div>
                </div>
                <Button type="submit" className="w-full bg-blue-600 hover:bg-blue-700 transition-colors" disabled={isSubmitting} data-testid="contact-submit-btn">
                  <Send className="mr-2 h-4 w-4" />
                  {isSubmitting ? 'Submitting...' : 'Submit Request'}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  );
};
